'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import BrandLogo from '@/components/ui/BrandLogo';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const quickLinks = [
  { label: 'Home', href: '/home', icon: '🏠' },
  { label: 'Destinations', href: '/destinations', icon: '🗺️' },
  { label: 'Packages', href: '/packages', icon: '📦' },
  { label: 'Hotels', href: '/hotels', icon: '🏨' },
  { label: 'Guides', href: '/guides', icon: '🧭' },
  { label: 'Support', href: '/search?q=help', icon: '💬' },
];

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const [mounted, setMounted] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(160deg, #1a0a0a 0%, #2d1010 35%, #1B3A6B 100%)' }}
    >
      {/* Top bar */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-white/5">
        <BrandLogo size={40} showTagline={false} />
        <button
          onClick={() => reset()}
          className="text-xs font-medium px-4 py-2 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-all"
        >
          ↺ Try Again
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div
          className="w-full max-w-2xl mx-auto text-center"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          {/* Storm / avalanche SVG illustration */}
          <div className="flex justify-center mb-8" aria-hidden="true">
            <svg
              width="260"
              height="140"
              viewBox="0 0 260 140"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Dark storm clouds */}
              <ellipse cx="80" cy="45" rx="55" ry="28" fill="#3d1515" opacity="0.7" />
              <ellipse cx="140" cy="35" rx="65" ry="32" fill="#4a1a1a" opacity="0.65" />
              <ellipse cx="200" cy="50" rx="50" ry="24" fill="#3d1515" opacity="0.6" />
              {/* Lightning bolt */}
              <path
                d="M138 38 L128 68 L136 68 L124 102 L148 62 L138 62 Z"
                fill="url(#lightningGrad)"
                opacity="0.9"
              />
              {/* Mountain silhouette */}
              <path
                d="M0 130 L70 70 L110 100 L155 45 L200 90 L260 130 Z"
                fill="url(#stormMtGrad)"
                opacity="0.4"
              />
              {/* Snow cap */}
              <path
                d="M155 45 L143 72 L155 65 L167 72 Z"
                fill="#F5F0E8"
                opacity="0.5"
              />
              {/* Horizon */}
              <line x1="0" y1="130" x2="260" y2="130" stroke="#E8621A" strokeWidth="1.5" opacity="0.25" />
              {/* Rain streaks */}
              <line x1="50" y1="80" x2="44" y2="105" stroke="#1ABCD4" strokeWidth="1" opacity="0.2" />
              <line x1="90" y1="75" x2="84" y2="100" stroke="#1ABCD4" strokeWidth="1" opacity="0.2" />
              <line x1="200" y1="78" x2="194" y2="103" stroke="#1ABCD4" strokeWidth="1" opacity="0.2" />
              <line x1="230" y1="72" x2="224" y2="97" stroke="#1ABCD4" strokeWidth="1" opacity="0.2" />
              <defs>
                <linearGradient id="lightningGrad" x1="136" y1="38" x2="136" y2="102" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#F5C030" />
                  <stop offset="1" stopColor="#E8621A" stopOpacity="0.6" />
                </linearGradient>
                <linearGradient id="stormMtGrad" x1="130" y1="45" x2="130" y2="130" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#5a2020" />
                  <stop offset="1" stopColor="#1B3A6B" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* 500 number */}
          <div
            className="font-serif font-bold leading-none mb-3 select-none"
            style={{
              fontSize: 'clamp(5rem, 18vw, 9rem)',
              background: 'linear-gradient(135deg, #E8621A, #F07830, #F5C030)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            500
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-white/20" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-white/30">
              Unexpected Storm
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-white/20" />
          </div>

          <h1 className="text-white font-bold text-2xl sm:text-3xl mb-3 font-serif">
            The Trail Hit a Rockslide
          </h1>
          <p className="text-white/50 text-sm sm:text-base mb-8 max-w-md mx-auto leading-relaxed">
            Something went wrong on our end — our Sherpas are already on it. Try refreshing the page or head back to safety.
          </p>

          {/* Primary actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <button
              onClick={() => reset()}
              className="btn-gold px-8 py-3 rounded-full text-sm font-semibold"
            >
              ↺ Retry the Route
            </button>
            <Link
              href="/home"
              className="btn-outline-gold px-8 py-3 rounded-full text-sm font-semibold inline-block"
            >
              🏔️ Return to Base Camp
            </Link>
          </div>

          {/* Error details (collapsible) */}
          {error?.message && (
            <div className="mb-10 max-w-md mx-auto">
              <button
                onClick={() => setShowDetails(v => !v)}
                className="text-xs text-white/25 hover:text-white/50 transition-colors flex items-center gap-1.5 mx-auto"
              >
                <span>{showDetails ? '▲' : '▼'}</span>
                <span>{showDetails ? 'Hide' : 'Show'} error details</span>
                {error.digest && (
                  <span className="ml-1 font-mono text-white/20">#{error.digest}</span>
                )}
              </button>
              {showDetails && (
                <div className="mt-3 p-3 rounded-xl border border-white/5 bg-white/3 text-left">
                  <p className="text-xs font-mono text-white/30 break-all leading-relaxed">
                    {error.message}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Quick nav grid */}
          <div className="border-t border-white/5 pt-8">
            <p className="text-xs text-white/30 uppercase tracking-widest mb-4 font-medium">
              Explore Instead
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 max-w-lg mx-auto">
              {quickLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border border-white/5 hover:border-sunrise/30 hover:bg-white/5 transition-all group"
                >
                  <span className="text-xl group-hover:scale-110 transition-transform">{link.icon}</span>
                  <span className="text-xs text-white/40 group-hover:text-sunrise transition-colors font-medium">
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer strip */}
      <footer className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
        <span className="text-xs text-white/20">© 2026 YatraNest Nepal</span>
        <span className="text-xs text-white/20">Our team has been notified ✦</span>
      </footer>
    </div>
  );
}
